import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router";
import { AppNav } from "../../components/AppNav";
import { Badge } from "../../components/Badge";
import { Layout } from "../../components/Layout";
import { Body, Display, Muted } from "../../components/Typography";
import { UnreadBadge } from "../../components/UnreadBadge";
import { ApiError, api } from "../../lib/api";
import type { CameraResponse, EdgeAgentResponse, NvrResponse, StoreResponse } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { AgentsPanel } from "./AgentsPanel";
import { BillingPanel } from "./BillingPanel";
import { MembersPanel } from "./MembersPanel";
import { ReportsPanel } from "./ReportsPanel";
import { SettingsPanel } from "./SettingsPanel";
import styles from "./StoreDetail.module.css";

type TabKey = "cameras" | "nvrs" | "agents" | "members" | "billing" | "settings" | "reports";
const TABS: readonly TabKey[] = [
  "cameras",
  "nvrs",
  "agents",
  "members",
  "billing",
  "settings",
  "reports",
];
const TAB_LABELS: Partial<Record<TabKey, string>> = { nvrs: "NVRs", agents: "Edge-Agents" };

const enabledCount = (items: CameraResponse[]): number => items.filter((i) => i.is_enabled).length;

// Desired-vs-actual (ADR 0017): is_enabled is the edge-reported actual; a pending or failed
// enable/disable request renders as such instead of the plain state.
export const cameraStateSub = (camera: CameraResponse): string => {
  if (camera.desired_enabled === null || camera.desired_enabled === camera.is_enabled) {
    return camera.is_enabled ? "enabled" : "disabled";
  }
  if (camera.desired_status === "pending" || camera.desired_status === "enqueued") {
    return camera.desired_enabled ? "enabling…" : "disabling…";
  }
  if (camera.desired_status === "failed") {
    return `${camera.is_enabled ? "enabled" : "disabled"} · change failed`;
  }
  return camera.is_enabled ? "enabled" : "disabled";
};

const aggregateStatus = (
  cameras: CameraResponse[],
  nvrs: NvrResponse[],
): { tone: "ok" | "warn"; label: string } => {
  const disabled = cameras.filter((c) => !c.is_enabled).length;
  if (disabled === 0) return { tone: "ok", label: nvrs.length > 0 ? "All enabled" : "No NVRs" };
  return { tone: "warn", label: `Camera disabled · ${disabled}` };
};

const CameraThumb = ({ camera }: { camera: CameraResponse }) => {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    // A single still for the grid preview (server GET /v1/live/{id}/snapshot). Mint a
    // one-shot view token and point the <img> at the snapshot URL derived from the live
    // stream_url. Unentitled/unreachable cameras keep the asset-id placeholder.
    Promise.resolve(api.createViewToken(camera.id))
      .then((r) => {
        if (alive && r?.stream_url) setSrc(r.stream_url.replace("?t=", "/snapshot?t="));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [camera.id]);

  if (!src) return <>{camera.asset_id}</>;
  return (
    <img
      className={styles.cameraThumbImg}
      src={src}
      alt={camera.name}
      loading="lazy"
      onError={() => setSrc(null)}
    />
  );
};

const CameraGrid = ({ cameras }: { cameras: CameraResponse[] }) => {
  if (cameras.length === 0) {
    return (
      <div className={styles.stub}>
        <Body>
          <Muted>
            No cameras yet — add or re-bind cameras from the store&apos;s Setup Panel (Camera
            Discovery); the edge reports its roster here.
          </Muted>
        </Body>
      </div>
    );
  }

  return (
    <>
      <div className={styles.cameraGrid}>
        {cameras.map((camera) => (
          <Link
            key={camera.id}
            to={`/cameras/${camera.id}`}
            className={styles.cameraCard}
            aria-label={camera.name}
          >
            <div className={styles.cameraThumb}>
              <CameraThumb camera={camera} />
            </div>
            <div className={styles.cameraMeta}>
              <span className={styles.cameraName}>{camera.name}</span>
              <span className={styles.cameraTrail}>
                <span className={styles.cameraSub}>{cameraStateSub(camera)}</span>
                <UnreadBadge count={camera.unread_alerts} />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </>
  );
};

const NvrList = ({ nvrs }: { nvrs: NvrResponse[] }) => {
  if (nvrs.length === 0) {
    return (
      <div className={styles.stub}>
        <Body>
          <Muted>No Network Video Recorders (NVRs) registered.</Muted>
        </Body>
      </div>
    );
  }

  return (
    <div className={styles.nvrList}>
      {nvrs.map((nvr) => (
        <Link key={nvr.id} to={`/nvrs/${nvr.id}`} className={styles.nvrRow}>
          <span aria-hidden="true" className={styles.nvrBadge}>
            {nvr.name.slice(0, 2).toUpperCase()}
          </span>
          <div>
            <div className={styles.nvrName}>{nvr.name}</div>
            <div className={styles.nvrSub}>
              {nvr.brand ?? "NVR"} · {nvr.host ?? "no host"}:{nvr.rtsp_port ?? "no RTSP"} ·{" "}
              {nvr.channel_count ?? "?"} channels
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
};

export const StoreDetail = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();

  const activeTab = (searchParams.get("tab") as TabKey | null) ?? "cameras";

  const [store, setStore] = useState<StoreResponse | null>(null);
  const [nvrs, setNvrs] = useState<NvrResponse[]>([]);
  const [cameras, setCameras] = useState<CameraResponse[]>([]);
  const [edgeAgents, setEdgeAgents] = useState<EdgeAgentResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!storeId) return;
    try {
      const [s, ns, cs, as] = await Promise.all([
        api.getStore(storeId),
        api.listStoreNvrs(storeId),
        api.listStoreCameras(storeId),
        api.listStoreEdgeAgents(storeId),
      ]);
      setStore(s);
      setNvrs(ns);
      setCameras(cs);
      setEdgeAgents(as);
    } catch (e) {
      setError(e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't load this store.");
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!storeId) return <Navigate to="/stores" replace />;

  const status = aggregateStatus(cameras, nvrs);

  const tabCount = (key: TabKey): number | null => {
    if (key === "cameras") return cameras.length;
    if (key === "nvrs") return nvrs.length;
    if (key === "agents") return edgeAgents.length;
    return null;
  };

  return (
    <Layout
      nav={
        <AppNav
          trail={[
            { label: "Your stores", href: "/stores" },
            { label: store?.name ?? (loading ? "Loading" : "Store") },
          ]}
          onSignOut={logout}
          userName={user?.display_name ?? user?.email}
          userAvatar={user?.avatar ?? null}
        />
      }
    >
      <main className={styles.content}>
        <section>
          <div className={styles.head}>
            <div>
              <div className={styles.titleRow}>
                <Display>{store?.name ?? (loading ? "…" : "Store not found")}</Display>
                {store && store.closed_at !== null ? (
                  <Badge variant="neutral" title={store.closed_reason ?? undefined}>
                    Closed
                  </Badge>
                ) : (
                  store && (
                    <Badge variant={status.tone === "ok" ? "success" : "warn"}>
                      {status.label}
                    </Badge>
                  )
                )}
              </div>
              {store && (
                <div className={styles.subline}>
                  {[store.address, store.timezone]
                    .filter((s): s is string => Boolean(s))
                    .join(" · ")}
                  {store.address || store.timezone ? " · " : ""}
                  {cameras.length} camera{cameras.length === 1 ? "" : "s"} across {nvrs.length} NVR
                  {nvrs.length === 1 ? "" : "s"}
                </div>
              )}
              {error && (
                <div role="alert" className={styles.subline}>
                  {error}
                </div>
              )}
            </div>

            {store && (
              <div className={styles.tiles}>
                <div className={styles.tile}>
                  <span className={styles.tileKicker}>Cameras</span>
                  <span className={styles.tileValue}>{enabledCount(cameras)} enabled</span>
                </div>
                <div className={styles.tile}>
                  <span className={styles.tileKicker}>NVRs</span>
                  <span className={styles.tileValue}>{nvrs.length}</span>
                </div>
                <div className={styles.tile}>
                  <span className={styles.tileKicker}>Alerts</span>
                  <span className={styles.tileValue}>{store.unread_alerts ?? 0} unread</span>
                </div>
              </div>
            )}
          </div>
        </section>

        <nav aria-label="Store sections" className={styles.tabbar}>
          {TABS.map((key) => {
            const count = tabCount(key);
            const label = TAB_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
            return (
              <Link
                key={key}
                to={`/stores/${storeId}?tab=${key}`}
                className={styles.tab}
                data-active={key === activeTab}
              >
                {label}
                {count !== null && <span className={styles.tabCount}>{count}</span>}
              </Link>
            );
          })}
        </nav>

        {loading ? (
          <div className={styles.status}>Loading…</div>
        ) : activeTab === "cameras" ? (
          <CameraGrid cameras={cameras} />
        ) : activeTab === "nvrs" ? (
          <NvrList nvrs={nvrs} />
        ) : activeTab === "agents" ? (
          <AgentsPanel
            storeId={storeId}
            agents={edgeAgents}
            cameras={cameras}
            onChanged={() => void load()}
          />
        ) : activeTab === "billing" ? (
          <BillingPanel storeId={storeId} store={store} currentUserId={user?.id} />
        ) : activeTab === "reports" ? (
          <ReportsPanel storeId={storeId} timeZone={store?.timezone ?? "UTC"} cameras={cameras} />
        ) : activeTab === "settings" ? (
          store && (
            <SettingsPanel
              key={store.id}
              store={store}
              currentUserId={user?.id}
              onStoreChanged={setStore}
            />
          )
        ) : (
          <MembersPanel
            storeId={storeId}
            store={store}
            currentUserId={user?.id}
            onOwnershipTransferred={() => void load()}
          />
        )}
      </main>
    </Layout>
  );
};
